import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Phone, MessageCircle, Edit2, Save, X, CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
export function UserProfile() {
  const {
    user,
    updateProfile
  } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: user?.email || '',
    cellNumber: user?.cellNumber || '',
    whatsappNumber: user?.whatsappNumber || ''
  });
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      await updateProfile(formData);
      setMessage('Profile updated successfully!');
      setIsEditing(false);
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };
  const handleCancel = () => {
    setFormData({
      email: user?.email || '',
      cellNumber: user?.cellNumber || '',
      whatsappNumber: user?.whatsappNumber || ''
    });
    setIsEditing(false);
    setMessage('');
  };
  if (!user) return null;
  return <div className="space-y-6" data-id="element-1106">
      {/* Success Message */}
      {message && <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} className={`p-4 rounded-xl flex items-center gap-3 ${message.includes('success') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-id="element-1107">
          {message.includes('success') && <CheckCircle className="w-5 h-5" data-id="element-1108" />}
          <span className="font-medium" data-id="element-1109">{message}</span>
        </motion.div>}

      <div className="bg-white rounded-2xl border border-rose-100 shadow-sm p-6" data-id="element-1110">
        <div className="flex items-center justify-between mb-6" data-id="element-1111">
          <h3 className="text-xl font-bold text-gray-900 font-serif flex items-center gap-2" data-id="element-1112">
            <User className="text-rose-500" size={24} data-id="element-1113" />
            Personal Information
          </h3>
          {!isEditing ? <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} leftIcon={<Edit2 className="w-4 h-4" data-id="element-1115" />} data-id="element-1114">
              Edit Profile
            </Button> : <div className="flex gap-2" data-id="element-1116">
              <Button variant="outline" size="sm" onClick={handleCancel} leftIcon={<X className="w-4 h-4" data-id="element-1118" />} data-id="element-1117">
                Cancel
              </Button>
              <Button size="sm" onClick={handleSubmit} disabled={loading} leftIcon={<Save className="w-4 h-4" data-id="element-1120" />} data-id="element-1119">
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </div>}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1121">
          {/* Name (Read-only) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1122">
            <div data-id="element-1123">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1124">
                First Name
              </label>
              <div className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900 font-medium" data-id="element-1125">
                {user.name}
              </div>
            </div>
            <div data-id="element-1126">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1127">
                Last Name
              </label>
              <div className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900 font-medium" data-id="element-1128">
                {user.surname}
              </div>
            </div>
          </div>

          {/* Email */}
          <div data-id="element-1129">
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2" data-id="element-1130">
              <Mail size={16} className="text-rose-500" data-id="element-1131" />
              Email Address
            </label>
            {isEditing ? <Input type="email" value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} placeholder="your.email@example.com" required data-id="element-1132" /> : <div className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900 font-medium" data-id="element-1133">
                {user.email}
              </div>}
          </div>

          {/* Cell Phone Number */}
          <div data-id="element-1134">
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2" data-id="element-1135">
              <Phone size={16} className="text-rose-500" data-id="element-1136" />
              Cell Phone Number
            </label>
            {isEditing ? <Input type="tel" value={formData.cellNumber} onChange={e => setFormData({
            ...formData,
            cellNumber: e.target.value
          })} placeholder="+27 82 345 6789" data-id="element-1137" /> : <div className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900 font-medium" data-id="element-1138">
                {user.cellNumber || 'Not provided'}
              </div>}
          </div>

          {/* WhatsApp Number */}
          <div data-id="element-1139">
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2" data-id="element-1140">
              <MessageCircle size={16} className="text-green-500" data-id="element-1141" />
              WhatsApp Number
            </label>
            {isEditing ? <Input type="tel" value={formData.whatsappNumber} onChange={e => setFormData({
            ...formData,
            whatsappNumber: e.target.value
          })} placeholder="+27 82 345 6789" data-id="element-1142" /> : <div className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900 font-medium" data-id="element-1143">
                {user.whatsappNumber || 'Not provided'}
              </div>}
            {isEditing && <p className="text-xs text-gray-500 mt-1" data-id="element-1144">
                This number will be used for order updates and customer support
              </p>}
          </div>

          {/* Region (Read-only) */}
          <div data-id="element-1145">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1146">
              Region
            </label>
            <div className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900 font-medium" data-id="element-1147">
              {user.region || 'Not specified'}
            </div>
          </div>
        </form>
      </div>

      {/* Account Info */}
      <div className="bg-gradient-to-br from-rose-50 to-purple-50 rounded-2xl border border-rose-100 p-6" data-id="element-1148">
        <h4 className="font-semibold text-gray-900 mb-4" data-id="element-1149">
          Account Information
        </h4>
        <div className="space-y-2 text-sm" data-id="element-1150">
          <div className="flex justify-between" data-id="element-1151">
            <span className="text-gray-600" data-id="element-1152">Account Type</span>
            <span className="font-medium text-gray-900 capitalize" data-id="element-1153">
              {user.role}
            </span>
          </div>
          <div className="flex justify-between" data-id="element-1154">
            <span className="text-gray-600" data-id="element-1155">Member Since</span>
            <span className="font-medium text-gray-900" data-id="element-1156">
              {new Date().toLocaleDateString('en-US', {
              month: 'long',
              year: 'numeric'
            })}
            </span>
          </div>
        </div>
      </div>
    </div>;
}